import type React from 'react';

import type { Text } from '@zillow/constellation';
import { FormField, Input, Label, Spacer } from '@zillow/constellation';

import FixedOrVariableSelector from '../Utilities/FixedOrVariableSelector';
import HeaderToggle from '../Utilities/HeaderToggle';
import type { Section } from '../../../../models/index';
import type { HTMLElementEvent } from '../../../../models/Types';
import { NATURAL_GAS, UTILITIES_INFORMATION } from '../../../../constants';
import { CenteredHR } from '../../../Widgets/CenteredHR';
import SwitchWithTitle from '../../../Widgets/SwitchWithTitle';
import RadioListBox from '../../../Widgets/RadioListBox';
import { UTILITIES_HEADING_AND_CONTENT } from '../../../../constants/stringConstants';

const INITIAL_STATE = {
  [NATURAL_GAS.FIELD_NAME.IS_ENABLED]: true,
  [NATURAL_GAS.FIELD_NAME.TENANT_PAYS_IN_ADDITION_TO_RENT]: false,
  [NATURAL_GAS.FIELD_NAME.FIXED_AMOUNT_SELECTED]: false,
  [NATURAL_GAS.FIELD_NAME.FIXED_AMOUNT_VALUE]: false,
  [NATURAL_GAS.FIELD_NAME.VARIABLE_COST_SELECTED]: false,
  [UTILITIES_INFORMATION.FIELD_NAME.NATURAL_GAS_PHONE_NUMBER]: null,
  [UTILITIES_INFORMATION.FIELD_NAME.NATURAL_GAS_SERVICE_PROVIDER]: null,
  [NATURAL_GAS.FIELD_NAME.SERVICE_PROVIDER]: null,
  [NATURAL_GAS.FIELD_NAME.LANDLORD_RESPONSIBILITY]: false,
  [NATURAL_GAS.FIELD_NAME.TENANT_RESPONSIBILITY]: false,
};

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

export default function NaturalGas_v4({ section, setSectionValues }: Props): JSX.Element {
  const option1 = NATURAL_GAS.FIELD_NAME.TENANT_RESPONSIBILITY;
  const option2 = NATURAL_GAS.FIELD_NAME.LANDLORD_RESPONSIBILITY;
  const naturalGas_isEnabled = section.getFieldValue(NATURAL_GAS.FIELD_NAME.IS_ENABLED);
  const naturalGas_tenantPaysInAdditionToRent = section.getFieldValue(
    NATURAL_GAS.FIELD_NAME.TENANT_PAYS_IN_ADDITION_TO_RENT,
  );
  const option2Selected = section.getFieldValue(option2);

  function setAdditionalFixedAmountValue(event: HTMLElementEvent<typeof Text>): void {
    setSectionValues({
      [NATURAL_GAS.FIELD_NAME.FIXED_AMOUNT_VALUE]: event.currentTarget.value,
    });
  }

  function setAdditionalFixedAmountSelected(): void {
    setSectionValues({
      [NATURAL_GAS.FIELD_NAME.FIXED_AMOUNT_SELECTED]: true,
      [NATURAL_GAS.FIELD_NAME.VARIABLE_COST_SELECTED]: false,
    });
  }

  function setServiceProvider(event: HTMLElementEvent<typeof Text>): void {
    setSectionValues({
      [UTILITIES_INFORMATION.FIELD_NAME.NATURAL_GAS_SERVICE_PROVIDER]: event.currentTarget.value,
    });
  }

  function toggleUtility(): void {
    setSectionValues({
      ...INITIAL_STATE,
      [NATURAL_GAS.FIELD_NAME.IS_ENABLED]: !section.getFieldValue(NATURAL_GAS.FIELD_NAME.IS_ENABLED),
    });
  }

  function togglePaysInAddition(): void {
    setSectionValues({
      [NATURAL_GAS.FIELD_NAME.TENANT_PAYS_IN_ADDITION_TO_RENT]: !section.getFieldValue(
        NATURAL_GAS.FIELD_NAME.TENANT_PAYS_IN_ADDITION_TO_RENT,
      ),
      [NATURAL_GAS.FIELD_NAME.FIXED_AMOUNT_SELECTED]: false,
      [NATURAL_GAS.FIELD_NAME.FIXED_AMOUNT_VALUE]: false,
      [NATURAL_GAS.FIELD_NAME.VARIABLE_COST_SELECTED]: false,
      [NATURAL_GAS.FIELD_NAME.SERVICE_PROVIDER]: '',
      [NATURAL_GAS.FIELD_NAME.LANDLORD_RESPONSIBILITY]: false,
      [NATURAL_GAS.FIELD_NAME.TENANT_RESPONSIBILITY]: false,
      [NATURAL_GAS.FIELD_NAME.FIXED_AMOUNT_VALUE]: '',
    });
  }

  function setAdditionalVariableCostSelected(): void {
    setSectionValues({
      [NATURAL_GAS.FIELD_NAME.FIXED_AMOUNT_VALUE]: 0,
      [NATURAL_GAS.FIELD_NAME.FIXED_AMOUNT_SELECTED]: false,
      [NATURAL_GAS.FIELD_NAME.VARIABLE_COST_SELECTED]: true,
    });
  }

  function renderOption2Details(): React.ReactNode {
    return (
      <Spacer marginTop="sm">
        <FixedOrVariableSelector
          legendText="What is the additional fee for natural gas?"
          fixedAmountSelected={section.getFieldValue(NATURAL_GAS.FIELD_NAME.FIXED_AMOUNT_SELECTED)}
          fixedAmountValue={section.getFieldValue(NATURAL_GAS.FIELD_NAME.FIXED_AMOUNT_VALUE)}
          setFixedAmountSelected={setAdditionalFixedAmountSelected}
          setFixedAmountValue={setAdditionalFixedAmountValue}
          setVariableMonthlySelected={setAdditionalVariableCostSelected}
          variableMonthlyCostSelected={section.getFieldValue(NATURAL_GAS.FIELD_NAME.VARIABLE_COST_SELECTED)}
        />
      </Spacer>
    );
  }

  function onServiceProviderPhoneNumberChange(event: HTMLElementEvent<typeof Text>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [UTILITIES_INFORMATION.FIELD_NAME.NATURAL_GAS_PHONE_NUMBER]: value,
    });
  }

  return (
    <>
      <HeaderToggle
        header="Natural gas"
        subheader="Applies to this property"
        switchOn={naturalGas_isEnabled}
        onSwitchToggle={toggleUtility}
      />
      {naturalGas_isEnabled && (
        <>
          <CenteredHR />
          <FormField
            marginBottom="sm"
            label={<Label>What service provider?</Label>}
            control={
              <Input
                placeholder="(example: National Natural Gas Co.)"
                value={section.getFieldValue(UTILITIES_INFORMATION.FIELD_NAME.NATURAL_GAS_SERVICE_PROVIDER)}
                onChange={setServiceProvider}
              />
            }
          />
          <FormField
            marginBottom="sm"
            label={<Label>What is the service provider’s number?</Label>}
            control={
              <Input
                type="tel"
                value={section.getFieldValue(UTILITIES_INFORMATION.FIELD_NAME.NATURAL_GAS_PHONE_NUMBER)}
                onChange={onServiceProviderPhoneNumberChange}
              />
            }
          />
          <SwitchWithTitle
            text="Tenant pays in addition to rent"
            onClick={togglePaysInAddition}
            toggled={naturalGas_tenantPaysInAdditionToRent}
          />
          {naturalGas_tenantPaysInAdditionToRent && (
            <Spacer marginTop="sm">
              {
                <>
                  <FormField
                    label={<Label>Who will be responsible for natural gas set up?</Label>}
                    control={
                      <RadioListBox
                        section={section}
                        setSectionValues={setSectionValues}
                        option1FieldName={option1}
                        option2FieldName={option2}
                        option1Header={UTILITIES_HEADING_AND_CONTENT.option1Heading}
                        option1Content={UTILITIES_HEADING_AND_CONTENT.option1Content}
                        option2Header={UTILITIES_HEADING_AND_CONTENT.option2Heading}
                        option2Content={UTILITIES_HEADING_AND_CONTENT.option2Content}
                      />
                    }
                  />
                  {option2Selected && renderOption2Details()}
                </>
              }
            </Spacer>
          )}
        </>
      )}
    </>
  );
}

NaturalGas_v4.description = 'Move service provider up';
